/* eslint-disable jsx-a11y/no-autofocus */

'use client';

import { Auth } from '~/modules/auth/client';
import { std } from '@funkey/shared';
import { usePostHog } from 'posthog-js/react';
import useCartState from '~/features/cart/useCartState';
import { processGraphqlFormErrors } from '~/utils/errors';
import useNotifications from '~/features/notifications/useNotifications';
import { usePortalCtx, usePortalRouter } from '~/features/global/portal';
import { useI18n } from '~/features/i18n';
import { useFormState, useValidateWithFeedback } from '~/modules/form';
import { GTM } from '~/features/google/tagmanager';
import { CheckoutAccountFormFields } from './CheckoutAccountPageForm';
import { CheckoutSourceFieldset } from './CheckoutSourceFieldset';
import { CheckoutSourceFormFields } from './checkoutSource';
import { CheckoutPageTemplate } from '../CheckoutPageTemplate';
import { VoucherCodeField } from './VoucherCodeField';
import { useTRPC } from '~/modules/trpc/client';
import { useMutation } from '@tanstack/react-query';
import assert from 'assert';
import { startTransition } from 'react';
import { redirect } from 'next/navigation';
import { PORTAL_RELATIVE_HREF_CART, PORTAL_RELATIVE_HREF_CHECKOUT } from '../../hooks';
export default function CheckoutAccountPageWithState() {
  const i18n = useI18n();
  const auth = Auth.useSession();
  const posthog = usePostHog();
  const trpc = useTRPC();
  const router = usePortalRouter();
  const notifications = useNotifications();
  const cart = useCartState();
  const cartQuery = cart.useCartSuspenseQuery();
  const portal = usePortalCtx();
  const isCheckoutSourceVisible = portal.portal !== 'BIZZ';
  const formState = useFormState<CheckoutAccountFormFields.FormValues & CheckoutSourceFormFields.FormValues & {
    voucherCode: React.ComponentProps<typeof VoucherCodeField>['value'];
  }>({
    firstName: auth.user?.firstName ?? '',
    lastName: auth.user?.lastName ?? '',
    email: auth.user?.email ?? '',
    phone: auth.user?.phone ?? '',
    businessName: portal.portal === 'BIZZ' ? portal.bizz.name : '',
    vatNumber: portal.portal === 'BIZZ' ? portal.bizz.vat || '' : '',
    voucherCode: null,
    ...CheckoutSourceFormFields.DEFAULT_VALUES
  }, {
    ...CheckoutAccountFormFields.FORM_VALIDATION(i18n),
    ...(isCheckoutSourceVisible ? CheckoutSourceFormFields.FORM_VALIDATION(i18n) : {})
  });
  const validateWithFeedback = useValidateWithFeedback(formState);
  const convertCart = cart.useConvertCartMutation();
  const createDeal = useMutation(trpc.deal.create.mutationOptions({
    throwOnError: false,
    async onSuccess() {
      router.push(`/checkout/confirmation`);
      // Is here so that we don't set the cart to `null` before the navigation is completed
      setTimeout(() => {
        startTransition(async () => {
          await convertCart.mutateAsync();
        });
      }, 100);
    },
    onError(error) {
      notifications.dispatchGraphqlError(error);
      formState.updateErrors(processGraphqlFormErrors(error));
    }
  }));
  if (cartQuery.data === null) {
    if (!createDeal.isSuccess) redirect(router.relativePath(PORTAL_RELATIVE_HREF_CART));
    return null;
  }
  const VOUCHER_FIELD = <VoucherCodeField value={formState.values.voucherCode} onChange={zID => formState.handleChange('voucherCode', zID)} />;
  return <CheckoutPageTemplate previousButton={{
    href: router.relativePath(PORTAL_RELATIVE_HREF_CHECKOUT('extra'))
  }} nextButton={{
    label: i18n._('checkout.cta.submit-quote', undefined, {
      message: 'Request a quote'
    }),
    async onClick() {
      if (!validateWithFeedback()) return;
      assert(cartQuery.data); // Component have been short circuited above

      const deal = await createDeal.mutateAsync({
        locale: i18n.locale,
        source: 'CHECKOUT',
        // [Attribution - saved to DB and synced to Zoho]
        checkoutSource: isCheckoutSourceVisible ? CheckoutSourceFormFields.toInput(formState.values) : null,
        // [User Info]
        contact: {
          firstName: formState.values.firstName,
          lastName: formState.values.lastName,
          email: formState.values.email,
          phone: formState.values.phone,
          optinSubscribeToNewsletter: false
        },
        customer: portal.portal === 'BIZZ' ? {
          portal: 'BIZZ',
          bizzID: portal.bizz.id
        } : {
          portal: 'PUBLIC',
          vatNumber: formState.values.vatNumber,
          businessName: formState.values.businessName
        },
        // [Deal Info]
        startsAt: cartQuery.data.startsAt!,
        endsAt: cartQuery.data.endsAt,
        participants: cartQuery.data.participants!,
        location: cartQuery.data.address ? {
          address: cartQuery.data.address,
          userProvided: cartQuery.data.userProvidesLocation!,
          radius: cartQuery.data.radius!
        } : null,
        budget: !!cartQuery.data.budget && !!cartQuery.data.budgetType ? {
          euro: cartQuery.data.budget,
          type: cartQuery.data.budgetType
        } : null,
        remarks: cartQuery.data.remarks,
        dateTimeIndication: cartQuery.data.meta.dateTimeIndication ?? null,
        durationType: cartQuery.data.durationType!,
        // [Items]
        items: cartQuery.data.items.map(x => ({
          variantID: x.activityVariantId!,
          languageID: x.activityLanguageId!,
          boostAdId: x.boostAdId
        })),
        // [Upselling]
        catering: {
          meta: std.Object.mapValues(cartQuery.data.meta.catering, catering => std.Object.omit(catering, ['ids'])),
          ids: Object.values(cartQuery.data.meta.catering).map(x_0 => x_0.ids).flat()
        },
        serviceIDs: [],
        // [Meta]
        voucherCode: formState.values.voucherCode
      });
      if (!auth.user) {
        posthog.identify(formState.values.email);
      } else if (auth.user.email !== formState.values.email) {
        posthog.alias(formState.values.email, auth.user.email);
      }
      GTM.setUserData(formState.values.email);
      GTM.sendEvent('funkeyCheckoutFinished', formState.values);

      // Track checkout with deal items for conversion tracking
      // This enables attribution for both organic and boosted conversions
      const dealItems = cartQuery.data.items.map(item => ({
        activityId: item.activityId,
        activityVariantId: item.activityVariantId,
        activityLanguageId: item.activityLanguageId,
        activityName: item.activity?.name,
        boostAdId: item.boostAdId
      }));
      posthog.capture('funkeyCheckoutFinished', {
        ...formState.values,
        source: 'checkout.cart',
        dealID: deal.id,
        // Deal items with full info for detailed conversion tracking
        dealItems,
        // Flat arrays for easy PostHog querying
        activityIds: dealItems.map(item_0 => item_0.activityId).filter(Boolean),
        activityVariantIds: dealItems.map(item_1 => item_1.activityVariantId).filter(Boolean),
        // Attribution tracking - derive checkout channel from cart items
        checkoutChannel: (() => {
          const hasBoostedItem = cartQuery.data.items.some(item_2 => !!item_2.boostAdId);
          if (hasBoostedItem) return 'boosted';
          if (portal.portal === 'BIZZ') return 'bizz';
          return 'organic';
        })(),
        portal: portal.portal
      });
    }
  }} sideBarContent={<div className="mt-8 hidden lg:block">{VOUCHER_FIELD}</div>}>
            <CheckoutAccountFormFields.FieldSet formState={formState.$subform<CheckoutAccountFormFields.FormValues>()} hiddenFields={portal.portal === 'BIZZ' ? ['businessName', 'vatNumber'] : []} />

            {isCheckoutSourceVisible && <div className="mt-10">
                    <CheckoutSourceFieldset formState={formState.$subform<CheckoutSourceFormFields.FormValues>()} />
                </div>}

            <div className="mt-8 lg:hidden">{VOUCHER_FIELD}</div>
        </CheckoutPageTemplate>;
}